/**
 * usePermissions Hook (Simplified V2 System)
 *
 * Convenience hook for common permission checking patterns.
 * Built on top of the PermissionContext.
 *
 * V2 Simplified System: Only 4 permission types total!
 * - System: admin, viewer
 * - Resource: read, write (write includes delete)
 */

import { useCallback } from 'react';
import { usePermissions as usePermissionContext } from '@/context/PermissionContext';
import {
  COMMON_CHECKS,
  createResourcePermissionCheck,
  createSystemPermissionCheck,
  type ResourcePermission,
  type ResourceType,
  SYSTEM_PERMISSIONS,
  type SystemPermission,
} from '@/types/permissions';

/**
 * Enhanced usePermissions hook with common permission check shortcuts
 */
export const usePermissions = () => {
  const context = usePermissionContext();

  // System permission checks
  const hasSystemPermission = useCallback(
    (permission: SystemPermission): boolean => {
      return context.checkPermission(createSystemPermissionCheck(permission));
    },
    [context]
  );

  // Resource permission checks
  const hasResourcePermission = useCallback(
    (resourceType: ResourceType, permission: ResourcePermission, resourceId?: string): boolean => {
      return context.checkPermission(
        createResourcePermissionCheck(resourceType, permission, resourceId)
      );
    },
    [context]
  );

  // System permission shortcuts (simplified V2 system - only 2 types!)
  const isAdmin = useCallback(
    () => hasSystemPermission(SYSTEM_PERMISSIONS.ADMIN),
    [hasSystemPermission]
  );

  const isViewer = useCallback(
    () => hasSystemPermission(SYSTEM_PERMISSIONS.VIEWER),
    [hasSystemPermission]
  );

  // Bulk permission checks with common patterns (simplified V2 system)
  const checkSystemPermissions = useCallback(() => {
    return context.checkPermissions([COMMON_CHECKS.ADMIN, COMMON_CHECKS.VIEWER]);
  }, [context]);

  return {
    // Core context methods
    ...context,

    // System permission checks (simplified V2 - only 2 types!)
    hasSystemPermission,
    isAdmin,
    isViewer,

    // Resource permission checks (simplified V2 - only 2 types!)
    hasResourcePermission,

    // Bulk checks (simplified V2)
    checkSystemPermissions,
  };
};
